import { useContext, useState } from 'react';
import { alpha, Box, List, styled, Button, ListItem, Collapse } from '@mui/material';
import { NavLink as RouterLink } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { useAuth, hasPermiso } from 'src/contexts/AuthContext';

import DesignServicesTwoToneIcon from '@mui/icons-material/DesignServicesTwoTone';
import BusinessTwoToneIcon from '@mui/icons-material/BusinessTwoTone';
import PeopleTwoToneIcon from '@mui/icons-material/PeopleTwoTone';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BadgeTwoToneIcon from '@mui/icons-material/BadgeTwoTone';
import SecurityTwoToneIcon from '@mui/icons-material/SecurityTwoTone';
import TableRestaurantTwoToneIcon from '@mui/icons-material/TableRestaurantTwoTone';
import MapTwoToneIcon from '@mui/icons-material/MapTwoTone';
import Inventory2TwoToneIcon from '@mui/icons-material/Inventory2TwoTone';
import CategoryTwoToneIcon from '@mui/icons-material/CategoryTwoTone';
import RestaurantTwoToneIcon from '@mui/icons-material/RestaurantTwoTone';
import ReceiptLongTwoToneIcon from '@mui/icons-material/ReceiptLongTwoTone';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import AssessmentTwoToneIcon from '@mui/icons-material/AssessmentTwoTone';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import PaymentIcon from '@mui/icons-material/Payment';
import TvIcon from '@mui/icons-material/Tv';

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};
    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }
  .MuiListSubheader-root {
    text-transform: uppercase;
    font-weight: bold;
    font-size: ${theme.typography.pxToRem(12)};
    color: ${theme.colors.alpha.trueWhite[50]};
    padding: ${theme.spacing(0, 2.5)};
    line-height: 1.4;
  }
  .sidebar-section-header {
    text-transform: uppercase;
    font-weight: bold;
    font-size: ${theme.typography.pxToRem(12)};
    color: ${theme.colors.alpha.trueWhite[50]};
    padding: ${theme.spacing(0.8, 2.5)};
    justify-content: space-between;
    min-height: 40px;
    &:hover { background-color: ${alpha(theme.sidebar.menuItemBgActive, 0.08)}; color: ${theme.colors.alpha.trueWhite[70]}; }
    .MuiSvgIcon-root { transition: transform 0.2s; }
    &.open .MuiSvgIcon-root { transform: rotate(180deg); }
  }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {
      .MuiListItem-root {
        padding: 1px 0;
        .MuiButton-root {
          display: flex;
          color: ${theme.sidebar.menuItemColor};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};
          border-radius: ${theme.general.borderRadiusSm};
          .MuiButton-startIcon { color: ${theme.colors.alpha.trueWhite[30]}; font-size: ${theme.typography.pxToRem(20)}; margin-right: ${theme.spacing(1)}; }
          .MuiButton-endIcon { color: ${theme.colors.alpha.trueWhite[50]}; margin-left: auto; opacity: .8; font-size: ${theme.typography.pxToRem(20)}; }
          &:hover {
            background-color: ${alpha(theme.sidebar.menuItemBgActive, 0.12)};
            color: ${theme.sidebar.menuItemColorActive};
            .MuiButton-startIcon, .MuiButton-endIcon { color: ${theme.sidebar.menuItemColorActive}; }
          }
          &.active {
            background-color: ${theme.sidebar.menuItemBgActive};
            color: ${theme.sidebar.menuItemColorActive};
            box-shadow: 0 0 0 1px ${alpha(theme.sidebar.menuItemBgActive, 0.4)};
            .MuiButton-startIcon, .MuiButton-endIcon { color: ${theme.sidebar.menuItemColorActive}; }
          }
        }
        .MuiCollapse-root {
          width: 100%;
          .MuiList-root { padding: ${theme.spacing(1, 0)}; }
          .MuiListItem-root {
            padding: 1px 0;
            .MuiButton-root { padding: ${theme.spacing(0.8, 3)}; }
          }
        }
      }
    }
`
);

const PERMISOS = {
  VER_DASHBOARD: 'VER_DASHBOARD',
  EMPRESA_VER: 'EMPRESA_VER',
  MONEDA_GESTION: 'MONEDA_GESTION',
  SUCURSAL_GESTION: 'SUCURSAL_GESTION',
  USUARIO_GESTION: 'USUARIO_GESTION',
  ROL_GESTION: 'ROL_GESTION',
  PERMISO_VER: 'PERMISO_VER',
  METODO_PAGO_GESTION: 'METODO_PAGO_GESTION',
  AREA_GESTION: 'AREA_GESTION',
  PRODUCTO_GESTION: 'PRODUCTO_GESTION',
  POS_USAR: 'POS_USAR',
  ZONA_GESTION: 'ZONA_GESTION',
  MESA_GESTION: 'MESA_GESTION',
  PEDIDO_CREAR: 'PEDIDO_CREAR',
  COMANDA_VER: 'COMANDA_VER',
  COMANDA_ACTUALIZAR: 'COMANDA_ACTUALIZAR',
  REPORTE_VER: 'REPORTE_VER',
  PEDIDO_ANULAR_VER: 'PEDIDO_ANULAR_VER',
  HISTORIAL_PEDIDO_VER: 'HISTORIAL_PEDIDO_VER',
  GESTION_TOKEN_TV: 'GESTION_TOKEN_TV',
} as const;

function SidebarMenu() {
  const { closeSidebar } = useContext(SidebarContext);
  const { user } = useAuth();
  const p = (codigo: string) => hasPermiso(user, codigo);

  const [openSection, setOpenSection] = useState<Record<string, boolean>>({
    admin: true,
    produccion: true,
    facturacion: true,
    comandas: true,
    reportes: true,
  });
  const toggle = (key: string) => setOpenSection((s) => ({ ...s, [key]: !s[key] }));

  const puedeVerEmpresas = user?.esSuperAdmin ?? !user?.empresa;
  const puedeVerMonedas = p(PERMISOS.MONEDA_GESTION);
  const puedeVerSucursales = p(PERMISOS.SUCURSAL_GESTION);
  const puedeVerUsuarios = p(PERMISOS.USUARIO_GESTION);
  const puedeVerRoles = p(PERMISOS.ROL_GESTION);
  const puedeVerPermisos = p(PERMISOS.PERMISO_VER);
  const puedeVerZonas = p(PERMISOS.ZONA_GESTION);
  const puedeVerMesas = p(PERMISOS.MESA_GESTION);
  const puedeVerMetodosPago = p(PERMISOS.METODO_PAGO_GESTION);
  const puedeGestionarTokensTv = p(PERMISOS.GESTION_TOKEN_TV);
  const puedeVerVentasDiarias = p(PERMISOS.REPORTE_VER);
  const puedeVerPedidosAnulados = p(PERMISOS.PEDIDO_ANULAR_VER);
  const puedeVerHistorialPedidos = p(PERMISOS.HISTORIAL_PEDIDO_VER);
  const showAdmin = puedeVerEmpresas || puedeVerMonedas || puedeVerSucursales || puedeVerUsuarios || puedeVerRoles || puedeVerPermisos || puedeVerMetodosPago || puedeGestionarTokensTv;
  const showProduccion = p(PERMISOS.AREA_GESTION) || p(PERMISOS.PRODUCTO_GESTION);
  const showFacturacion = p(PERMISOS.POS_USAR) || puedeVerZonas || puedeVerMesas;
  const showComandas = p(PERMISOS.COMANDA_VER);
  const showReportes = puedeVerVentasDiarias || puedeVerPedidosAnulados || puedeVerHistorialPedidos;


  return (
    <MenuWrapper>
      <List component="div">
        <SubMenuWrapper>
          <List component="div">
            <ListItem component="div">
              <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/" startIcon={<DesignServicesTwoToneIcon />}>
                {p(PERMISOS.VER_DASHBOARD) ? 'Dashboard' : 'Inicio'}
              </Button>
            </ListItem>
          </List>
        </SubMenuWrapper>
      </List>

      {showAdmin && (
        <List component="div">
          <ListItem component="div" disablePadding>
            <Button fullWidth disableRipple className={`sidebar-section-header ${openSection.admin ? 'open' : ''}`} onClick={() => toggle('admin')} endIcon={<ExpandMoreIcon />}>
              Administración
            </Button>
          </ListItem>
          <Collapse in={openSection.admin} timeout="auto" unmountOnExit>
            <SubMenuWrapper>
              <List component="div" disablePadding>
                {puedeVerMonedas && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/administracion/monedas" startIcon={<AttachMoneyIcon />}>Monedas</Button>
                  </ListItem>
                )}
                {puedeVerEmpresas && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/administracion/empresas" startIcon={<BusinessTwoToneIcon />}>Empresas</Button>
                  </ListItem>
                )}                
                {puedeVerSucursales && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/administracion/sucursales" startIcon={<StorefrontIcon />}>Sucursales</Button>
                  </ListItem>
                )}
                {puedeVerPermisos && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/administracion/permisos" startIcon={<SecurityTwoToneIcon />}>Permisos</Button>
                  </ListItem>
                )}
                {puedeVerRoles && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/administracion/roles" startIcon={<BadgeTwoToneIcon />}>Roles</Button>
                  </ListItem>
                )}                
                {puedeVerUsuarios && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/administracion/usuarios" startIcon={<PeopleTwoToneIcon />}>Usuarios</Button>
                  </ListItem>
                )}
                {puedeVerMetodosPago && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/administracion/metodos-pago" startIcon={<PaymentIcon />}>Métodos de pago</Button>
                  </ListItem>
                )}
                {puedeGestionarTokensTv && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/administracion/tokens-tv" startIcon={<TvIcon />}>Tokens TV</Button>
                  </ListItem>
                )}
              </List>
            </SubMenuWrapper>
          </Collapse>
        </List>
      )}

      {showProduccion && (
        <List component="div">
          <ListItem component="div" disablePadding>
            <Button fullWidth disableRipple className={`sidebar-section-header ${openSection.produccion ? 'open' : ''}`} onClick={() => toggle('produccion')} endIcon={<ExpandMoreIcon />}>
              Producción
            </Button>
          </ListItem>
          <Collapse in={openSection.produccion} timeout="auto" unmountOnExit>
            <SubMenuWrapper>
              <List component="div" disablePadding>
                {p(PERMISOS.AREA_GESTION) && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/produccion/areas" startIcon={<TableRestaurantTwoToneIcon />}>Áreas</Button>
                  </ListItem>
                )}
                {p(PERMISOS.PRODUCTO_GESTION) && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/produccion/categorias" startIcon={<CategoryTwoToneIcon />}>Categorías</Button>
                  </ListItem>
                )}
                {p(PERMISOS.PRODUCTO_GESTION) && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/produccion/productos" startIcon={<Inventory2TwoToneIcon />}>Productos</Button>
                  </ListItem>
                )}
              </List>
            </SubMenuWrapper>
          </Collapse>
        </List>
      )}

      {showFacturacion && (
        <List component="div">
          <ListItem component="div" disablePadding>
            <Button fullWidth disableRipple className={`sidebar-section-header ${openSection.facturacion ? 'open' : ''}`} onClick={() => toggle('facturacion')} endIcon={<ExpandMoreIcon />}>
              Punto de venta
            </Button>
          </ListItem>
          <Collapse in={openSection.facturacion} timeout="auto" unmountOnExit>
            <SubMenuWrapper>
              <List component="div" disablePadding>
                {puedeVerZonas && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/facturacion/zonas" startIcon={<MapTwoToneIcon />}>Zonas</Button>
                  </ListItem>
                )}
                {puedeVerMesas && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/facturacion/mesas" startIcon={<RestaurantTwoToneIcon />}>Mesas</Button>
                  </ListItem>
                )}
                {p(PERMISOS.POS_USAR) && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/facturacion/pos" startIcon={<PointOfSaleIcon />}>POS</Button>
                  </ListItem>
                )}
                {p(PERMISOS.COMANDA_ACTUALIZAR) && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/facturacion/pedidos-listos" startIcon={<NotificationsActiveTwoToneIcon />}>Pedidos listos</Button>
                  </ListItem>
                )}
              </List>
            </SubMenuWrapper>
          </Collapse>
        </List>
      )}

      {showComandas && (
        <List component="div">
          <ListItem component="div" disablePadding>
            <Button fullWidth disableRipple className={`sidebar-section-header ${openSection.comandas ? 'open' : ''}`} onClick={() => toggle('comandas')} endIcon={<ExpandMoreIcon />}>
              Comandas
            </Button>
          </ListItem>
          <Collapse in={openSection.comandas} timeout="auto" unmountOnExit>
            <SubMenuWrapper>
              <List component="div" disablePadding>
                <ListItem component="div">
                  <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/comandas/por-producto" startIcon={<RestaurantMenuIcon />}>Por producto</Button>
                </ListItem>
                <ListItem component="div">
                  <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/comandas/por-pedido" startIcon={<RestaurantMenuIcon />}>Por pedido</Button>
                </ListItem>
              </List>
            </SubMenuWrapper>
          </Collapse>
        </List>
      )}

      {showReportes && (
        <List component="div">
          <ListItem component="div" disablePadding>
            <Button fullWidth disableRipple className={`sidebar-section-header ${openSection.reportes ? 'open' : ''}`} onClick={() => toggle('reportes')} endIcon={<ExpandMoreIcon />}>
              Reportes
            </Button>
          </ListItem>
          <Collapse in={openSection.reportes} timeout="auto" unmountOnExit>
            <SubMenuWrapper>
              <List component="div" disablePadding>
                {puedeVerVentasDiarias && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/reportes/ventas-diarias" startIcon={<AssessmentTwoToneIcon />}>Ventas diarias</Button>
                  </ListItem>
                )}
                {puedeVerHistorialPedidos && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/reportes/historial" startIcon={<ReceiptLongTwoToneIcon />}>Historial de pedidos</Button>
                  </ListItem>
                )}
                {puedeVerPedidosAnulados && (
                  <ListItem component="div">
                    <Button disableRipple component={RouterLink} onClick={closeSidebar} to="/reportes/pedidos-anulados" startIcon={<AssessmentTwoToneIcon />}>Pedidos anulados</Button>
                  </ListItem>
                )}
              </List>
            </SubMenuWrapper>
          </Collapse>
        </List>
      )}
    </MenuWrapper>
  );
}

export default SidebarMenu;
